/**
 * Browser-held BYOK OpenAI key (client-only pivot, Phase 2).
 *
 * The key lives in the user's browser and nowhere else:
 * - in-memory holder for the current tab (always)
 * - localStorage when "remember on this device" is enabled (default)
 *
 * The key is the ONE thing the product persists; user content (audio,
 * transcripts, notes) is memory-only by design. Never log the key —
 * masked hints only (`maskKey`).
 */
import { useSyncExternalStore } from "react";
import { logWarning } from "@/lib/logging";

const KEY_STORAGE_KEY = "wendai.openai-key";
const BASE_URL_STORAGE_KEY = "wendai.openai-base-url";

export const DEFAULT_OPENAI_BASE_URL = "https://api.openai.com/v1";

/** Timeout for the browser-side validation ping. */
const VALIDATE_TIMEOUT_MS = 10_000;

let runtimeKey: string | null = null;
let runtimeBaseUrl: string | null = null;
let hydrated = false;

const listeners = new Set<() => void>();

function notifyKeyListeners(): void {
  listeners.forEach((listener) => listener());
}

function subscribeToKey(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Reactive key presence for components. SSR snapshot is `false`;
 * `useSyncExternalStore` re-reads the client snapshot after hydration,
 * so a remembered key flips the UI without a hydration mismatch.
 */
export function useHasOpenAIKey(): boolean {
  return useSyncExternalStore(
    subscribeToKey,
    () => getOpenAIKey() !== null,
    () => false,
  );
}

/** Reactive masked display hint (`sk-…abcd`), or null when no key is set. */
export function useOpenAIKeyHint(): string | null {
  return useSyncExternalStore(
    subscribeToKey,
    () => {
      const key = getOpenAIKey();
      return key === null ? null : maskKey(key);
    },
    () => null,
  );
}

function safeLocalStorage(): Storage | null {
  // localStorage throws in some privacy modes; the app must keep working
  // with a session-only key when it does.
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function hydrateFromStorage(): void {
  if (hydrated) return;
  hydrated = true;
  const store = safeLocalStorage();
  if (!store) return;
  try {
    runtimeKey = store.getItem(KEY_STORAGE_KEY);
    runtimeBaseUrl = store.getItem(BASE_URL_STORAGE_KEY);
  } catch {
    logWarning("[byok] Could not read the stored key from localStorage");
  }
}

/** The key OpenAI calls should use right now, or null when unset. */
export function getOpenAIKey(): string | null {
  hydrateFromStorage();
  return runtimeKey;
}

/** Custom OpenAI-compatible endpoint; defaults to the official API. */
export function getOpenAIBaseUrl(): string {
  hydrateFromStorage();
  return runtimeBaseUrl || DEFAULT_OPENAI_BASE_URL;
}

/** @internal Test-only export. */
export function hasOpenAIKey(): boolean {
  return getOpenAIKey() !== null;
}

export interface StoreKeyOptions {
  /** Persist to localStorage (device-remember). Default true. */
  remember?: boolean;
  /** Custom OpenAI-compatible base URL; empty/undefined = official API. */
  baseUrl?: string;
}

/**
 * Set the key for this tab and (optionally) remember it on this device.
 * Callers should validate first (`validateOpenAIKey`).
 */
export function storeOpenAIKey(key: string, options: StoreKeyOptions = {}): void {
  const { remember = true, baseUrl } = options;
  hydrateFromStorage();
  runtimeKey = key;
  runtimeBaseUrl = baseUrl?.trim() ? normalizeBaseUrl(baseUrl) : null;

  const store = safeLocalStorage();
  if (store) {
    try {
      if (remember) {
        store.setItem(KEY_STORAGE_KEY, key);
        if (runtimeBaseUrl) store.setItem(BASE_URL_STORAGE_KEY, runtimeBaseUrl);
        else store.removeItem(BASE_URL_STORAGE_KEY);
      } else {
        store.removeItem(KEY_STORAGE_KEY);
        store.removeItem(BASE_URL_STORAGE_KEY);
      }
    } catch {
      logWarning("[byok] Could not persist the key to localStorage — it will last for this tab only");
    }
  }
  notifyKeyListeners();
}

/** Forget the key everywhere (memory + device). */
export function clearOpenAIKey(): void {
  hydrateFromStorage();
  runtimeKey = null;
  runtimeBaseUrl = null;
  const store = safeLocalStorage();
  if (store) {
    try {
      store.removeItem(KEY_STORAGE_KEY);
      store.removeItem(BASE_URL_STORAGE_KEY);
    } catch {
      logWarning("[byok] Could not remove the key from localStorage");
    }
  }
  notifyKeyListeners();
}

/** `sk-…abcd` style display hint. Never render the raw key. */
export function maskKey(key: string): string {
  if (key.length <= 8) return "•••";
  return `${key.slice(0, 3)}…${key.slice(-4)}`;
}

/** Trim trailing slashes so `${base}/models` composes cleanly. */
export function normalizeBaseUrl(url: string): string {
  return url.trim().replace(/\/+$/, "");
}

export type KeyValidationResult =
  | { ok: true }
  | { ok: false; reason: "unauthorized" | "network" };

/**
 * Validate a key by listing models directly from the browser.
 * 401/403 → "unauthorized" (bad key) — EXCEPT when the error body says the
 * key is merely missing the models scope: OpenAI restricted keys without
 * "Models: Read" get 401 `insufficient_permissions` here yet transcribe
 * fine, so they count as OK. Anything else that fails → "network"
 * (unreachable endpoint, CORS, timeout). Other HTTP errors (e.g. 429) count
 * as OK — the endpoint recognized the key.
 */
export async function validateOpenAIKey(
  key: string,
  baseUrl?: string,
): Promise<KeyValidationResult> {
  const base = baseUrl?.trim() ? normalizeBaseUrl(baseUrl) : DEFAULT_OPENAI_BASE_URL;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), VALIDATE_TIMEOUT_MS);
  try {
    const response = await fetch(`${base}/models`, {
      headers: { Authorization: `Bearer ${key}` },
      signal: controller.signal,
    });
    if (response.status === 401 || response.status === 403) {
      return (await isScopedKeyError(response))
        ? { ok: true }
        : { ok: false, reason: "unauthorized" };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: "network" };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * True when a 401/403 body carries OpenAI's `insufficient_permissions`
 * code — the endpoint recognized the key; it just can't list models.
 * Non-JSON or differently-shaped bodies (invalid_api_key, plain proxies)
 * stay "unauthorized".
 */
async function isScopedKeyError(response: Response): Promise<boolean> {
  try {
    const body: unknown = await response.json();
    const code =
      typeof body === "object" && body !== null
        ? (body as { error?: { code?: unknown } }).error?.code
        : undefined;
    return code === "insufficient_permissions";
  } catch {
    return false;
  }
}

/** @internal test-only reset of module state. */
export function __resetOpenAIKeyForTests(): void {
  runtimeKey = null;
  runtimeBaseUrl = null;
  hydrated = false;
}
